// ===================== Level-up upgrade cards =====================
// Generates the 3-4 cards offered on each level-up: learn/upgrade weapons,
// take passives (Multiplication is the headline), or adopt/level an element.
import { Weapons, Elements, getWeapon } from '../core/registries.js';
import { EVOLUTIONS } from './evolutions.js';
import { Audio } from '../engine/audio.js';
import { Save } from '../engine/save.js';

const MAX_WEAPONS = 6;
const MAX_PASSIVES = 6;
const MAX_ELEMENT_LEVEL = 8;

// ---- Passive stat upgrades ----
export const PASSIVES = [
  {
    id: 'multiply', name: 'Multiplication', icon: '✖', max: 5, rarity: 'epic',
    tag: 'Multiply', tagClass: 'tag-multiply',
    // ×1.25 over 5 picks caps card-bought Multiplication at ~×3.05 (was
    // ×1.35 × 6 = ×6.05 — modelled as roughly half the old ceiling). `max`
    // counts CARDS ONLY: the golden ✖ pickup stacks on top of this ceiling
    // and never spends one of the five, so collecting one is never a mistake.
    // `desc` reports p.projMult, which is both channels combined.
    desc: (p) => `Multiplies every weapon's projectile count. ×1.25 (now ×${(p.projMult).toFixed(2)}).`,
    apply: (p) => { p.projMult *= 1.25; },
  },
  {
    id: 'might', name: 'Might', icon: '⚔', max: 8, rarity: 'common',
    tag: 'Passive', tagClass: 'tag-passive',
    desc: () => '+14% damage dealt.',
    apply: (p) => { p.damageMult *= 1.14; },
  },
  {
    id: 'haste', name: 'Haste', icon: '⏱', max: 8, rarity: 'common',
    tag: 'Passive', tagClass: 'tag-passive',
    desc: () => '-10% weapon cooldown (attack faster).',
    apply: (p) => { p.cooldownMult = Math.max(0.35, p.cooldownMult * 0.90); },
  },
  {
    id: 'growth', name: 'Growth', icon: '⊕', max: 8, rarity: 'common',
    tag: 'Passive', tagClass: 'tag-passive',
    desc: () => '+15% area of effect.',
    apply: (p) => { p.areaMult *= 1.15; },
  },
  {
    id: 'swift', name: 'Swift Boots', icon: '»', max: 6, rarity: 'common',
    tag: 'Passive', tagClass: 'tag-passive',
    desc: () => '+10% movement speed.',
    apply: (p) => { p.moveSpeed *= 1.10; },
  },
  {
    id: 'vitality', name: 'Vitality', icon: '♥', max: 8, rarity: 'common',
    tag: 'Passive', tagClass: 'tag-passive',
    desc: () => '+25 max HP and heal 25.',
    apply: (p) => { p.maxHp += 25; p.heal(25); },
  },
  {
    id: 'regen', name: 'Regeneration', icon: '✚', max: 6, rarity: 'rare',
    tag: 'Passive', tagClass: 'tag-passive',
    desc: () => '+1.2 HP regenerated per second.',
    apply: (p) => { p.regen += 1.2; },
  },
  {
    id: 'armor', name: 'Iron Skin', icon: '🛡', max: 6, rarity: 'rare',
    tag: 'Passive', tagClass: 'tag-passive',
    desc: () => '+2 armor (flat damage reduction).',
    apply: (p) => { p.armor += 2; },
  },
  {
    id: 'magnet', name: 'Lodestone', icon: '🧲', max: 5, rarity: 'common',
    tag: 'Passive', tagClass: 'tag-passive',
    desc: () => '+25% pickup range.',
    apply: (p) => { p.pickupRadius *= 1.25; },
  },
  {
    id: 'velocity', name: 'Velocity', icon: '➹', max: 6, rarity: 'common',
    tag: 'Passive', tagClass: 'tag-passive',
    desc: () => '+15% projectile speed.',
    apply: (p) => { p.projSpeedMult *= 1.15; },
  },
  {
    id: 'fortune', name: 'Fortune', icon: '✷', max: 6, rarity: 'rare',
    tag: 'Passive', tagClass: 'tag-passive',
    desc: () => '+5% crit chance, +0.25 crit damage.',
    apply: (p) => { p.critChance += 0.05; p.critMult += 0.25; },
  },
  {
    id: 'greed', name: 'Greed', icon: '💰', max: 5, rarity: 'rare',
    tag: 'Passive', tagClass: 'tag-passive',
    desc: () => '+20% gold gained.',
    apply: (p) => { p.goldMult *= 1.20; },
  },
  {
    id: 'wisdom', name: 'Wisdom', icon: '✧', max: 5, rarity: 'rare',
    tag: 'Passive', tagClass: 'tag-passive',
    desc: () => '+15% experience gained.',
    apply: (p) => { p.xpMult *= 1.15; },
  },
  {
    id: 'duplicate', name: 'Duplicate', icon: '⧉', max: 4, rarity: 'epic',
    tag: 'Multiply', tagClass: 'tag-multiply',
    desc: (p) => `+1 projectile to every weapon (now +${p.projAdd + 1}).`,
    apply: (p) => { p.projAdd += 1; },
  },
  {
    id: 'duration', name: 'Spellbind', icon: '∞', max: 6, rarity: 'rare',
    tag: 'Passive', tagClass: 'tag-passive',
    desc: () => '+12% effect duration (auras, pools, DoT).',
    apply: (p) => { p.durationMult *= 1.12; },
  },
  {
    id: 'curse', name: 'Curse', icon: '☠', max: 5, rarity: 'rare',
    tag: 'Curse', tagClass: 'tag-element',
    desc: (p) => `+1 Curse (now ${p.curse + 1}): stronger, faster, more numerous foes — but richer rewards.`,
    apply: (p) => { p.curse += 1; },
  },
  {
    id: 'phoenix', name: 'Phoenix Heart', icon: '✵', max: 2, rarity: 'epic',
    tag: 'Revive', tagClass: 'tag-multiply',
    desc: () => 'Cheat death once: revive at 50% HP and clear the screen.',
    apply: (p) => { p.reviveCount += 1; },
  },
];

const passiveById = Object.fromEntries(PASSIVES.map((p) => [p.id, p]));

// The first weapon that can currently evolve (maxed + paired passive owned), or null.
export function availableEvolution(player) {
  for (const w of player.weapons) {
    const evo = EVOLUTIONS[w.def.id];
    if (!evo || w.level < w.def.maxLevel || player.passiveLevel(evo.req) < 1) continue;
    const evoDef = getWeapon(evo.into);
    if (evoDef) return { baseId: w.def.id, into: evo.into, def: evoDef };
  }
  return null;
}

const RARITY_WEIGHT = { common: 1.0, rare: 0.5, epic: 0.28 };

function rarityWeight(rarity, luck) {
  const base = RARITY_WEIGHT[rarity] ?? 1;
  if (rarity === 'common') return base;
  return base * (1 + luck * 0.25);   // luck favors better cards
}

// Build the full candidate list for the current player state.
function candidates(game) {
  const p = game.player;
  const out = [];

  // Weapons — upgrade owned, or learn new.
  for (const w of p.weapons) {
    if (w.level < w.def.maxLevel) {
      out.push({
        key: 'w-' + w.def.id, type: 'weapon', rarity: 'common',
        title: w.def.name, tag: 'Weapon ▲', tagClass: 'tag-weapon',
        icon: w.def.icon, sub: `Lv ${w.level} → ${w.level + 1}`,
        desc: w.def.desc,
        apply: (g) => g.player.upgradeWeapon(w.def.id),
      });
    }
  }
  if (p.weaponCount() < MAX_WEAPONS) {
    for (const def of Weapons.values()) {
      if (def.evolution) continue;        // evolved weapons are never "learned"
      if (def.ultra) continue;            // ultraweapons only drop from Alphas
      if (p.hasWeapon(def.id)) continue;
      out.push({
        key: 'w-' + def.id, type: 'weapon', rarity: 'rare',
        title: def.name, tag: 'New Weapon', tagClass: 'tag-weapon',
        icon: def.icon, sub: 'Learn',
        desc: def.desc,
        apply: (g) => g.player.addWeapon(def.id),
      });
    }
  }

  // Evolutions — base weapon maxed AND its paired passive owned.
  for (const w of p.weapons) {
    const evo = EVOLUTIONS[w.def.id];
    if (!evo || w.level < w.def.maxLevel || p.passiveLevel(evo.req) < 1) continue;
    const evoDef = getWeapon(evo.into);
    if (!evoDef) continue;
    const reqPassive = passiveById[evo.req];
    out.push({
      key: 'evo-' + evo.into, type: 'evolution', rarity: 'epic',
      title: 'EVOLVE → ' + evoDef.name, tag: 'Evolution', tagClass: 'tag-evolution',
      icon: evoDef.icon, sub: `${w.def.name} + ${reqPassive ? reqPassive.name : evo.req}`,
      desc: evoDef.desc,
      apply: (g) => {
        g.player.evolveWeapon(w.def.id, evo.into, getWeapon);
        g.setAnnounce('✦ ' + evoDef.name + ' ✦', 2.8);
        g.camera.shake(8, 0.3);
        if (g.particles_burst) g.particles_burst(g.player.x, g.player.y, '#ff8a3d', 28);
        if (g.unlockAch) g.unlockAch('evolved');
        Audio.multiply();
      },
    });
  }

  // Passives (respect the 6-slot cap for NEW passives; owned ones always upgrade).
  for (const pa of PASSIVES) {
    const lvl = p.passiveLevel(pa.id);
    if (lvl >= pa.max) continue;
    if (lvl === 0 && p.passives.size >= MAX_PASSIVES) continue;
    out.push({
      key: 'p-' + pa.id, type: 'passive', rarity: pa.rarity,
      title: pa.name, tag: pa.tag, tagClass: pa.tagClass,
      icon: pa.icon, sub: lvl > 0 ? `Lv ${lvl} → ${lvl + 1}` : 'New',
      desc: pa.desc(p),
      apply: (g) => { pa.apply(g.player); g.player.bumpPassive(pa.id); },
    });
  }

  // Elements.
  if (p.element === 'neutral') {
    for (const el of Elements.values()) {
      // Ascendant (tier-2) forms are only ever reached through the ASCEND
      // card below — offering them as level-1 attunements would skip the
      // whole mastery arc.
      if (el.id === 'neutral' || el.tier === 2) continue;
      out.push({
        key: 'e-' + el.id, type: 'element', rarity: 'rare',
        title: 'Attune: ' + el.name, tag: 'Element', tagClass: 'tag-element',
        icon: el.short, sub: 'Adopt affinity',
        desc: el.desc,
        apply: (g) => { g.player.element = el.id; g.player.elementLevel = 1; g.setAnnounce('Attuned to ' + el.name, 2.2); },
      });
    }
  } else if (p.elementLevel < MAX_ELEMENT_LEVEL) {
    const el = Elements.get(p.element);
    out.push({
      key: 'e-up', type: 'element', rarity: 'rare',
      title: el.name + ' Mastery', tag: 'Element ▲', tagClass: 'tag-element',
      icon: el.short, sub: `Lv ${p.elementLevel} → ${p.elementLevel + 1}`,
      desc: 'Deepen your ' + el.name + ' affinity — stronger on-hit effects.',
      apply: (g) => { g.player.elementLevel++; },
    });
  }

  // Ascendant transform — a mastered element (L8) plus a mastered weapon
  // (any maxed base weapon, or any evolution owned; ultraweapons don't count)
  // offers the second elemental tier. Taking it REPLACES the element with its
  // Ascendant form; the ascended def has no ascendsTo (and is already at max
  // level), so both this card and element mastery evaporate afterwards.
  const curEl = Elements.get(p.element);
  if (curEl && curEl.ascendsTo && p.elementLevel >= MAX_ELEMENT_LEVEL) {
    const asc = Elements.get(curEl.ascendsTo);
    const weaponReady = p.weapons.some((w) => w.def.evolution || (!w.def.ultra && w.level >= w.def.maxLevel));
    if (asc && weaponReady) {
      out.push({
        key: 'asc-' + curEl.id, type: 'ascendant', rarity: 'epic',
        title: 'ASCEND → ' + asc.name, tag: 'ASCEND', tagClass: 'tag-ascend',
        icon: asc.short, sub: curEl.name + ' Apotheosis',
        desc: asc.desc,
        apply: (g) => {
          g.player.element = asc.id;         // elementLevel stays 8
          g.setAnnounce('★ ' + asc.name + ' ★', 2.8);
          g.camera.shake(8, 0.3);            // no-ops under reduced motion
          g.addFlash(asc.color, 0.5);
          if (g.particles_burst) g.particles_burst(g.player.x, g.player.y, asc.color, 28);
          if (g.unlockAch) g.unlockAch('element_ascend');
          // The COLLECTION is tracked across runs (Hexarch) — never the
          // ascension itself, which is per-run like everything else.
          if (!Save.data.seen.includes('asc:' + asc.id)) {
            Save.data.seen.push('asc:' + asc.id);
            Save.flush();
          }
          const tier2 = [...Elements.values()].filter((e) => e.tier === 2);
          if (tier2.length && tier2.every((e) => Save.data.seen.includes('asc:' + e.id))) {
            if (g.unlockAch) g.unlockAch('element_ascend_all');
          }
          Audio.multiply();
        },
      });
    }
  }

  return out;
}

// Base weapons eligible for the weapon-commitment pity: L4+, not yet maxed,
// neither evolved nor ultra. Highest level first — the closest payoff.
function pityWeapons(p) {
  return p.weapons
    .filter((w) => !w.def.evolution && !w.def.ultra && w.level >= 4 && w.level < w.def.maxLevel)
    .sort((a, b) => b.level - a.level);
}

// Roll N distinct cards, weighted by rarity and luck.
// opts.banished: Set of card keys to exclude for this run.
export function rollUpgrades(game, n = 3, opts = {}) {
  const p = game.player;
  const banished = opts.banished || new Set();
  if (p.luck >= 3) n = 4;
  let pool = candidates(game).filter((c) => !banished.has(c.key));
  const picked = [];
  const used = new Set();

  // Pinned cards surface ahead of the random roll, capped so at least one
  // random slot always remains (at n=3 that's at most two pins).
  const pin = (c) => {
    if (!c || used.has(c.key) || picked.length >= n - 1) return;
    picked.push(c); used.add(c.key);
    pool = pool.filter((x) => x !== c);
  };

  // Always surface the build payoffs — an available evolution first, then the
  // Ascendant transform.
  pin(pool.find((c) => c.type === 'evolution'));
  pin(pool.find((c) => c.type === 'ascendant'));

  // COMMITMENT PITY — commit to your element and the cards will come. The
  // counters live on the Game (reset in start() alongside rerolls/banishes);
  // the policy lives here in the content layer. A card missing from the last
  // 2 hands gets pinned, guaranteeing it at least every 3rd hand.
  // Mastery pity: while attuned below max level.
  if ((game.elemPity || 0) >= 2) pin(pool.find((c) => c.key === 'e-up'));
  // Weapon pity: once any base weapon reaches L4+, its upgrade card gets the
  // same guarantee — highest-level weapon first.
  if ((game.weapPity || 0) >= 2) {
    for (const w of pityWeapons(p)) {
      const card = pool.find((c) => c.key === 'w-' + w.def.id);
      if (card) { pin(card); break; }
    }
  }

  let guard = 0;
  while (picked.length < n && pool.length > 0 && guard++ < 200) {
    const choice = game.rng.weighted(pool, (c) => rarityWeight(c.rarity, p.luck));
    if (used.has(choice.key)) {
      // remove to avoid infinite loop when few candidates remain
      const idx = pool.indexOf(choice); if (idx >= 0) pool.splice(idx, 1);
      continue;
    }
    used.add(choice.key);
    picked.push(choice);
    const idx = pool.indexOf(choice); if (idx >= 0) pool.splice(idx, 1);
  }

  // Book-keep the pity counters from what this hand actually offered.
  const offered = (k) => picked.some((c) => c.key === k);
  if (p.element !== 'neutral' && p.elementLevel < MAX_ELEMENT_LEVEL) {
    game.elemPity = offered('e-up') ? 0 : (game.elemPity || 0) + 1;
  } else game.elemPity = 0;
  const pw = pityWeapons(p);
  if (pw.length) {
    game.weapPity = pw.some((w) => offered('w-' + w.def.id)) ? 0 : (game.weapPity || 0) + 1;
  } else game.weapPity = 0;

  // Limit Break — once the offer pool is completely empty (every weapon maxed,
  // every passive capped, the element ascended), level-ups keep boosting you
  // instead of showing nothing. It is the graceful empty-pool handler, not an
  // achievement trigger: an empty pool implies six finished weapons, so
  // Perfected's live rule (content/achievements.js) has already fired long
  // before anyone gets here.
  if (picked.length === 0) {
    const breaks = [
      { t: 'Overpower', d: '+8% damage, heal fully.', a: (pl) => { pl.damageMult *= 1.08; pl.heal(pl.maxHp); } },
      { t: 'Overflow', d: '+1 projectile, heal fully.', a: (pl) => { pl.projAdd += 1; pl.heal(pl.maxHp); } },
      { t: 'Overgrowth', d: '+8% area, heal fully.', a: (pl) => { pl.areaMult *= 1.08; pl.heal(pl.maxHp); } },
      { t: 'Overclock', d: '-6% cooldown, heal fully.', a: (pl) => { pl.cooldownMult = Math.max(0.3, pl.cooldownMult * 0.94); pl.heal(pl.maxHp); } },
    ];
    const b = breaks[p.level % breaks.length];
    picked.push({
      key: 'limitbreak', type: 'limitbreak', rarity: 'epic',
      title: 'LIMIT BREAK: ' + b.t, tag: 'Limit Break', tagClass: 'tag-multiply', icon: '✦', sub: 'Build maxed',
      desc: b.d + ' ◈+' + 20,
      apply: (g) => { b.a(g.player); g.gold += 20; },
    });
  }
  return picked;
}
